// src/pages/Today.tsx — "What needs me right now", one action per item (OPS-FINAL D2).
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { canApply, ops, type ContactChannel, type FlywheelItem, type FlywheelItemKind, type LeadDetail, type ProcQueueItem, type QueueItem, type QueueKind, type TodayData } from "../lib/ops";
import { dateTime, waited } from "../lib/format";
import { useOperator, useToast } from "../lib/context";
import { Loading, Notice, PageFrame } from "../components/ui";
import { Panel } from "../components/Panel";
import { ProcurementPanel } from "../components/ProcurementPanel";

const MAX_ROWS = 10;

const KIND_LABEL: Record<QueueKind, string> = {
  job_unassigned: "Paid job with no Maestro",
  lead_waiting: "Lead waiting over 4 hours",
  refusals_today: "Booking refused today",
  job_no_photos: "Completed job with no photos",
  counter_needs_approval: "Counter-offer needs your approval",
};

const ACTION_LABEL: Record<QueueKind, string> = {
  job_unassigned: "Assign in Dispatch →",
  lead_waiting: "Mark contacted →",
  refusals_today: "Review service →",
  job_no_photos: "Open job →",
  counter_needs_approval: "Review in Dispatch →",
};

type Filter = "all" | "jobs" | "leads";

// OPS-METRICS-01: route flywheel items (ops_flywheel_today), shown in their own section like supplier orders.
const ROUTE_KIND_LABEL: Record<FlywheelItemKind, string> = {
  route_cooling: "Route cooling",
  route_lit: "Route lit",
  campaign_cost_missing: "Campaign cost missing",
  jobs_unassigned: "Jobs without a route",
};
const ROUTE_ACTION_LABEL: Record<FlywheelItemKind, string> = {
  route_cooling: "Open the route",
  route_lit: "Plan the next route",
  campaign_cost_missing: "Record the cost",
  jobs_unassigned: "Open Dispatch",
};

const REFUSAL_WORDS: Record<string, string> = {
  service_out_of_season: "out of season",
  service_unavailable_in_region: "not offered in that area",
  service_inactive: "switched off",
  service_not_found: "service not found",
  no_price_configured: "no price set",
  zip_required: "no ZIP given",
  promo_invalid: "promotion not valid",
  date_in_past: "date in the past",
  date_invalid: "invalid date",
};

/** "Porch Flowers — service_out_of_season" → "Porch Flowers: out of season". */
function itemTitle(item: QueueItem): string {
  if (item.kind !== "refusals_today") return item.title;
  const code = item.target_id.split(":")[1] ?? "";
  const name = item.title.split(" — ")[0];
  return `${name}: ${REFUSAL_WORDS[code] ?? code.replace(/_/g, " ")}`;
}

export default function Today() {
  const navigate = useNavigate();
  const [data, setData] = useState<TodayData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [lead, setLead] = useState<QueueItem | null>(null);
  const [procs, setProcs] = useState<ProcQueueItem[]>([]);
  const [proc, setProc] = useState<ProcQueueItem | null>(null);
  const [procError, setProcError] = useState<string | null>(null);
  const [routeItems, setRouteItems] = useState<FlywheelItem[]>([]);
  const [routeError, setRouteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await ops.today());
      try {
        setProcs(await ops.procurementQueue());
        setProcError(null);
      } catch (e) {
        setProcError(`Supplier orders didn't load: ${(e as Error).message}`);
      }
      try {
        setRouteItems((await ops.flywheelToday()).items);
        setRouteError(null);
      } catch (e) {
        setRouteError(`Route items didn't load: ${(e as Error).message}`);
      }
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  function actRoute(item: FlywheelItem) {
    if ((item.kind === "route_cooling" || item.kind === "route_lit") && item.zip) navigate(`/territories?zip=${item.zip}`);
    else if (item.kind === "campaign_cost_missing" && item.campaign_id) navigate(`/campaigns?campaign=${encodeURIComponent(item.campaign_id)}`);
    else navigate("/dispatch");
  }

  function act(item: QueueItem) {
    if (item.kind === "job_unassigned") navigate(`/dispatch?job=${item.target_id}&action=assign`);
    else if (item.kind === "job_no_photos" || item.kind === "counter_needs_approval") navigate(`/dispatch?job=${item.target_id}`);
    else if (item.kind === "refusals_today") navigate(`/pricing/services?open=${encodeURIComponent(item.target_id.split(":")[0])}`);
    else setLead(item);
  }

  const items = (data?.items ?? []).filter(
    (i) => filter === "all" || (filter === "jobs" ? i.kind === "job_unassigned" : i.kind === "lead_waiting"),
  );
  const shown = showAll ? items : items.slice(0, MAX_ROWS);
  const h = data?.header;

  const panel = proc ? (
    <ProcurementPanel
      orderId={proc.order_id}
      title={`${proc.supplier} · ${proc.po_number}`}
      onClose={() => setProc(null)}
      onDone={async () => {
        setProc(null);
        await load();
      }}
    />
  ) : lead ? (
    <LeadPanel
      item={lead}
      onClose={() => setLead(null)}
      onDone={async () => {
        setLead(null);
        await load();
      }}
    />
  ) : undefined;

  return (
    <PageFrame title="Today" lede="What needs you right now, most urgent first." panel={panel}>
      {error && <Notice kind="error">{error}</Notice>}
      {procError && <Notice kind="error">{procError}</Notice>}
      {routeError && <Notice kind="error">{routeError}</Notice>}
      {!data && !error && <Loading what="today's queue" />}
      {h && (
        <div className="counts" role="group" aria-label="Today's counts">
          <button type="button" className={filter === "jobs" ? "count-link on" : "count-link"} aria-pressed={filter === "jobs"} onClick={() => setFilter(filter === "jobs" ? "all" : "jobs")}>
            <span className="count-num">{h.jobs_needing_maestro}</span>
            <span className="count-label">{h.jobs_needing_maestro === 1 ? "job needs a Maestro" : "jobs need a Maestro"}</span>
          </button>
          <button type="button" className={filter === "leads" ? "count-link on" : "count-link"} aria-pressed={filter === "leads"} onClick={() => setFilter(filter === "leads" ? "all" : "leads")}>
            <span className="count-num">{h.leads_waiting_4h}</span>
            <span className="count-label">{h.leads_waiting_4h === 1 ? "lead waiting over 4 h" : "leads waiting over 4 h"}</span>
          </button>
          <button type="button" className="count-link" onClick={() => navigate("/dispatch")}>
            <span className="count-num">{h.active_jobs}</span>
            <span className="count-label">{h.active_jobs === 1 ? "active job" : "active jobs"}</span>
          </button>
        </div>
      )}

      {procs.length > 0 && (
        <section className="proc-queue" aria-label="Supplier orders">
          <h2 className="proc-queue-title">Supplier orders</h2>
          <ol className="queue">
            {procs.map((p) => (
              <li key={p.order_id} className={`queue-row kind-proc ${p.blocking > 0 ? "proc-blocked" : ""}`}>
                <div className="queue-what">
                  <span className="queue-kind">
                    {p.blocking > 0 ? `Quote blocked (${p.blocking})` : p.status === "needs_review" ? "Quote needs review" : "Quote ready to approve"}
                    {p.flags > 0 ? ` · ${p.flags} flag${p.flags === 1 ? "" : "s"}` : ""}{p.test_mode ? " · TEST" : ""}
                  </span>
                  <span className="queue-title">
                    {p.supplier} {p.location ?? ""} · {p.po_number}{p.quote_total != null ? ` · $${Number(p.quote_total).toFixed(2)}` : ""}
                  </span>
                </div>
                <span className="queue-zip mono">{p.gm_code}</span>
                <span className="queue-wait" title={dateTime(p.waiting_since)}>{waited(p.waiting_since)}</span>
                <button type="button" className="btn primary queue-action" onClick={() => { setLead(null); setProc(p); }}>Review quote →</button>
              </li>
            ))}
          </ol>
        </section>
      )}

      {routeItems.length > 0 && (
        <section className="proc-queue" aria-label="Routes">
          <h2 className="proc-queue-title">Routes</h2>
          <ol className="queue">
            {routeItems.map((r) => (
              <li key={`${r.kind}:${r.zip ?? ""}:${r.route ?? ""}:${r.campaign_id ?? ""}`} className={`queue-row kind-${r.kind}`}>
                <div className="queue-what">
                  <span className="queue-kind">{ROUTE_KIND_LABEL[r.kind]}</span>
                  <span className="queue-title">{r.title.replace(/ [🟢🔴]$/u, "")}</span>
                  <span className="muted">{r.detail}</span>
                </div>
                <span className="queue-zip mono">{r.route ? `${r.zip} ${r.route}` : r.zip ?? ""}</span>
                <span className="queue-wait" />
                <button type="button" className="btn primary queue-action" onClick={() => actRoute(r)}>
                  {ROUTE_ACTION_LABEL[r.kind]}
                </button>
              </li>
            ))}
          </ol>
        </section>
      )}

      {data && items.length === 0 && <p className="empty-state">Nothing needs you right now.</p>}
      {data && items.length > 0 && (
        <ol className="queue">
          {shown.map((item) => (
            <li key={`${item.kind}:${item.target_id}`} className={`queue-row kind-${item.kind}`}>
              <div className="queue-what">
                <span className="queue-kind">{KIND_LABEL[item.kind]}</span>
                <span className="queue-title">
                  {itemTitle(item)}
                  {item.count && item.count > 1 ? ` (${item.count})` : ""}
                </span>
              </div>
              <span className="queue-zip mono">{item.zip ?? ""}</span>
              <span className="queue-wait" title={dateTime(item.waiting_since)}>
                {waited(item.waiting_since)}
              </span>
              <button type="button" className="btn primary queue-action" onClick={() => act(item)}>
                {ACTION_LABEL[item.kind]}
              </button>
            </li>
          ))}
        </ol>
      )}
      {data && !showAll && items.length > MAX_ROWS && (
        <button type="button" className="btn quiet" onClick={() => setShowAll(true)}>
          Show all {items.length}
        </button>
      )}
    </PageFrame>
  );
}

const CHANNELS: { id: ContactChannel; label: string }[] = [
  { id: "phone", label: "Phone call" },
  { id: "email", label: "Email" },
  { id: "in_person", label: "In person" },
  { id: "text", label: "Text message" },
];

function LeadPanel(props: { item: QueueItem; onClose: () => void; onDone: () => Promise<void> }) {
  const { role } = useOperator();
  const toast = useToast();
  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [channel, setChannel] = useState<ContactChannel | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    ops
      .leadDetail(props.item.target_id)
      .then((l) => live && setLead(l))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [props.item.target_id]);

  const contact = lead?.contact ?? "";
  const isEmail = contact.includes("@");
  const allowed = canApply(role);

  async function save() {
    if (!channel) return;
    setBusy(true);
    setError(null);
    try {
      await ops.markLeadContacted(props.item.target_id, channel, note.trim());
      toast("✓ Marked contacted");
      await props.onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel kicker="Lead" title={props.item.title} onClose={props.onClose}>
      {!lead && !error && <Loading what="the lead" />}
      {error && <Notice kind="error">{error}</Notice>}
      {lead && (
        <>
          <dl className="facts">
            <dt>Waiting</dt>
            <dd>{waited(lead.created_at)} (since {dateTime(lead.created_at)})</dd>
            <dt>Contact</dt>
            <dd>
              {contact ? <a href={isEmail ? `mailto:${contact}` : `tel:${contact.replace(/[^\d+]/g, "")}`}>{contact}</a> : "Not given"}
            </dd>
            <dt>Address</dt>
            <dd>{lead.address ?? "—"}</dd>
            {lead.budget && (
              <>
                <dt>Budget</dt>
                <dd>{lead.budget}</dd>
              </>
            )}
            {lead.intents && lead.intents.length > 0 && (
              <>
                <dt>Interested in</dt>
                <dd>{lead.intents.join(", ")}</dd>
              </>
            )}
            <dt>Texting</dt>
            <dd>{lead.sms_consent ? "Consented" : "No consent recorded, so don't text"}</dd>
          </dl>

          <fieldset className="choice">
            <legend>How did you reach them?</legend>
            {CHANNELS.filter((c) => c.id !== "text" || lead.sms_consent).map((c) => (
              <label key={c.id}>
                <input type="radio" name="channel" checked={channel === c.id} onChange={() => setChannel(c.id)} />
                {c.label}
              </label>
            ))}
          </fieldset>
          <textarea
            className="reason"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What happened? (optional, goes in the change log)"
            aria-label="Note"
          />
          {allowed ? (
            <button type="button" className="btn primary wide" disabled={!channel || busy} onClick={save}>
              {busy ? "Saving…" : "Mark contacted"}
            </button>
          ) : (
            <Notice kind="info">Viewers can see leads but not mark them contacted.</Notice>
          )}
        </>
      )}
    </Panel>
  );
}
